import { useTranslation } from "react-i18next";
import { ArrowLeft, BoxSelect, MousePointerClick, Trash2 } from "lucide-react";
import clsx from "clsx";
import { Button } from "components/common/Button";
import { ConfirmActionModal } from "../ConfirmActionModal";
import { AreaRow } from "./AreaRow";
import { SelectionAreasLegend } from "./SelectionAreasLegend";
import type { SelectionAreasModeProps } from "./types";

export const SelectionAreasMode = ({ state }: SelectionAreasModeProps) => {
  const { t } = useTranslation();
  const {
    step,
    areas,
    draftPoints,
    isDraftFinalized,
    isSaving,
    selectedTarget,
    selectArea,
    startDrawing,
    finalizeDraft,
    saveDraft,
    requestDiscardDraft,
    toggleAreaFlag,
    setAreaMode,
    requestDeleteArea,
    confirmDeleteArea,
    confirmDiscard,
    modal,
    closeModal,
    requestClose,
  } = state;

  const isDrawing = step === "drawing";
  const hasDraft = draftPoints !== null && isDraftFinalized;
  const hasAreas = areas.length > 0;
  const isDraftSelected = selectedTarget?.type === "draft";

  return (
    <div className="w-full h-full bg-white rounded-lg border border-gray-200 shadow-sm p-4 overflow-hidden flex flex-col gap-3">
      <div className="w-full">
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={requestClose}
            startAdornment={<ArrowLeft className="w-4 h-4" />}
            aria-label={t("common.back")}
          />
          <h2 className="text-sm font-semibold text-gray-900">
            {t("smartSurveyor.selectionAreas.title")}
          </h2>
        </div>
        <SelectionAreasLegend />
        {!hasDraft && (
          <div className="mt-3 w-full flex items-center gap-2">
            <Button
              variant={isDrawing ? "primary" : "outline"}
              size="sm"
              onClick={isDrawing ? finalizeDraft : startDrawing}
              startAdornment={<MousePointerClick className="w-3.5 h-3.5" />}
            >
              {isDrawing
                ? t("smartSurveyor.selectionAreas.finishArea")
                : t("smartSurveyor.selectionAreas.drawArea")}
            </Button>
          </div>
        )}
      </div>
      {isDrawing && (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-gray-400">
          <BoxSelect className="w-10 h-10 text-blue-500" />
          <p className="text-sm text-gray-500 text-center px-3">
            {t("smartSurveyor.selectionAreas.drawingHint")}
          </p>
        </div>
      )}
      {!isDrawing && (
        <div className="flex-1 overflow-y-auto flex flex-col gap-2">
          {hasDraft && (
            <div
              className={clsx(
                "flex items-center gap-2 p-3 rounded-md border border-dashed cursor-pointer",
                isDraftSelected
                  ? "border-blue-400 bg-blue-50"
                  : "border-blue-300 hover:bg-blue-50",
              )}
              onClick={() => selectArea("draft")}
            >
              <span className="text-base font-medium text-blue-900 flex-1">
                {t("smartSurveyor.selectionAreas.draftLabel")}
              </span>
              <Button
                variant="primary"
                size="sm"
                disabled={isSaving}
                onClick={(event) => {
                  event.stopPropagation();
                  saveDraft();
                }}
              >
                {isSaving
                  ? t("smartSurveyor.selectionAreas.saving")
                  : t("smartSurveyor.selectionAreas.save")}
              </Button>
              <Button
                variant="text"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  requestDiscardDraft();
                }}
                className="!p-0 !text-gray-400 hover:!text-red-600"
              >
                <Trash2 size={18} />
              </Button>
            </div>
          )}
          {!hasAreas && !hasDraft && (
            <div className="flex-1 flex flex-col items-center justify-center gap-2 text-gray-400">
              <BoxSelect className="w-10 h-10 text-red-500" />
              <p className="text-sm text-gray-500 text-center px-3">
                {t("smartSurveyor.selectionAreas.emptyState")}
              </p>
            </div>
          )}
          {areas.map((area, index) => (
            <AreaRow
              key={area.id}
              area={area}
              index={index + 1}
              isSelected={
                selectedTarget?.type === "saved" &&
                selectedTarget.id === area.id
              }
              onSelect={selectArea}
              onToggleFlag={toggleAreaFlag}
              onSetMode={setAreaMode}
              onRequestDelete={requestDeleteArea}
            />
          ))}
        </div>
      )}
      <ConfirmActionModal
        isOpen={modal?.type === "deletePolygon"}
        onClose={closeModal}
        onConfirm={confirmDeleteArea}
        action="delete"
        title={t("smartSurveyor.selectionAreas.deleteModal.title")}
        description={t("smartSurveyor.selectionAreas.deleteModal.description")}
      />
      <ConfirmActionModal
        isOpen={modal?.type === "discardDraft"}
        onClose={closeModal}
        onConfirm={confirmDiscard}
        action="delete"
        title={t("smartSurveyor.selectionAreas.discardModal.title")}
        description={t("smartSurveyor.selectionAreas.discardModal.description")}
      />
    </div>
  );
};
